import { breakpoints, Input, Stack, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { PASSENGERS } from '../../constants/formFieldNames';
import { passengerFields } from '../../constants/formFields';
import {
  getAdultsToggleConstraints,
  getChildrenToggleConstraints,
  getInfantsToggleConstraints,
} from '../../utils/passengers';
import { useFormattedChildrenAges } from '../../utils/useFormattedChildrenAges';
import { usePassengers } from '../../utils/usePassengers';
import type { SearchFormValues } from '../SeachFormNewContainer';
import PassengerRowNew from './PassengerRowNew';
import PassengersToggle from './PassengersToggle';

const wrapperStyles = css`
  flex: 1;
  min-width: 0;
`;

const passengersWrapperStyles = css`
  margin-inline: -16px;

  @media (min-width: ${breakpoints._768}) {
    margin: 0;
  }
`;

const agesStyles = css`
  background-color: #f0f0f0;
  border-top: 1px solid ${vars.colors.divider};
  padding: 8px 16px;
`;

type PassengersNewProps = {
  constraints?: {
    maxNumberOfTravelers: number;
    isDisabled: boolean;
  };
};

const PassengersNew = ({ constraints }: PassengersNewProps) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { watch } = useFormContext<SearchFormValues>();
  const values = watch(PASSENGERS);
  const { label } = usePassengers(values);
  const childrenAgesLabel = useFormattedChildrenAges(values);
  const buttonId = 'passengers-toggle-button';

  const onLabelClick = () => {
    buttonRef.current?.focus();
  };

  return (
    <Stack className={wrapperStyles} spacing={4}>
      <Input.Label htmlFor={buttonId} onClick={onLabelClick}>
        {t('Searchform.Passengers.Label')}
      </Input.Label>
      <PassengersToggle
        buttonId={buttonId}
        childrenAgesLabel={childrenAgesLabel}
        isDisabled={constraints?.isDisabled}
        label={label}
        ref={buttonRef}
        title={t('Searchform.Passengers.Label')}
      >
        <div className={passengersWrapperStyles}>
          <PassengerRowNew
            fieldName={`${PASSENGERS}.${passengerFields.ADULTS}`}
            label={t('Air.Search.Adults')}
            toggleConstraints={getAdultsToggleConstraints(values, constraints)}
          />
          <PassengerRowNew
            fieldName={`${PASSENGERS}.${passengerFields.CHILDREN}`}
            label={t('Air.Search.Children')}
            toggleConstraints={getChildrenToggleConstraints(values, constraints)}
          />
          <PassengerRowNew
            constraints={constraints}
            description={t('Searchform.Passengers.Infants.Information')}
            fieldName={`${PASSENGERS}.${passengerFields.INFANTS}`}
            label={t('Air.Search.Infants')}
            toggleConstraints={getInfantsToggleConstraints(values)}
          />
          {values.children > 0 && (
            <>
              <div className={agesStyles}>
                {t('Responsive.Air.SearchFormPage.PassengersPane.ChildAgeInfo')}
              </div>
              {Array.from({ length: values.children }).map((_, index) => (
                <PassengerRowNew
                  constraints={constraints}
                  fieldName={`${PASSENGERS}.${passengerFields.CHILDREN_AGES}`}
                  key={`child-${index}`}
                  label={t('Air.Search.ChildAge', index! + 1)}
                  passengerIndex={index}
                />
              ))}
            </>
          )}
        </div>
      </PassengersToggle>
    </Stack>
  );
};

export default PassengersNew;
